import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FocusScope } from '@react-aria/focus';
import {
  DismissButton,
  useOverlay,
  usePreventScroll
} from '@react-aria/overlays';
import cn from 'classnames';

import { DEFAULT_STYLES } from '../../constants';

import { Portal } from '../Portal';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  className?: string;
  showCloseButton?: boolean;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  children,
  className,
  showCloseButton = true
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isClosingRef = useRef(false); // Prevent multiple close calls
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);

  // Prevent body scroll when bottom sheet is open - handled by react-aria
  usePreventScroll({ isDisabled: !isOpen });

  // Visual Viewport API to handle keyboard appearance on mobile
  useEffect(() => {
    if (!isOpen) return;

    const updateAvailableHeight = () => {
      // Use Visual Viewport API if available (modern mobile browsers)
      // This API automatically accounts for keyboard height
      if (window.visualViewport) {
        const viewportHeight = window.visualViewport.height;
        // Visual Viewport height already excludes keyboard
        // Ensure minimum usable space (30% of original viewport)
        const minHeight = window.innerHeight * 0.3;
        setAvailableHeight(Math.max(viewportHeight, minHeight));
      } else {
        // Fallback for browsers without Visual Viewport API
        // Use a conservative estimate (80% of window height)
        const windowHeight = window.innerHeight;
        setAvailableHeight(Math.max(windowHeight * 0.8, windowHeight * 0.3));
      }
    };

    // Initial calculation
    updateAvailableHeight();

    // Listen to visual viewport resize events (keyboard show/hide)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateAvailableHeight);
      window.visualViewport.addEventListener('scroll', updateAvailableHeight);
    } else {
      // Fallback: listen to window resize
      window.addEventListener('resize', updateAvailableHeight);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          'resize',
          updateAvailableHeight
        );
        window.visualViewport.removeEventListener(
          'scroll',
          updateAvailableHeight
        );
      } else {
        window.removeEventListener('resize', updateAvailableHeight);
      }
    };
  }, [isOpen]);

  // Scroll focused input into view when keyboard appears
  useEffect(() => {
    if (!isOpen || !contentRef.current) return;

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      // Check if focused element is an input, textarea, or contenteditable
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        // Use requestAnimationFrame to ensure DOM is updated
        requestAnimationFrame(() => {
          // Scroll the focused element into view
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        });
      }
    };

    const contentElement = contentRef.current;
    contentElement.addEventListener('focusin', handleFocusIn);

    return () => {
      contentElement.removeEventListener('focusin', handleFocusIn);
    };
  }, [isOpen]);

  // Reset closing flag when opening
  useEffect(() => {
    if (isOpen) {
      isClosingRef.current = false;
    }
  }, [isOpen]);

  // Safe close handler that prevents multiple calls
  const handleClose = useCallback(() => {
    if (isClosingRef.current) return; // Already closing
    isClosingRef.current = true;
    onClose();
    // Reset after a brief delay to allow unmount
    setTimeout(() => {
      isClosingRef.current = false;
    }, 300);
  }, [onClose]);

  // Overlay behavior from react-aria
  const { overlayProps, underlayProps } = useOverlay(
    {
      isOpen,
      onClose: handleClose,
      isDismissable: true,
      shouldCloseOnBlur: false
    },
    overlayRef
  );

  // Handle overlay click to close
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  if (!isOpen) return null;

  return (
    <Portal>
      <div
        {...underlayProps}
        ref={overlayRef}
        className={DEFAULT_STYLES.bottomSheet__overlay}
        onClick={handleOverlayClick}
      >
        {/* Backdrop with fade animation */}
        <div
          className={cn(
            DEFAULT_STYLES.bottomSheet__backdrop,
            isOpen ? 'opacity-50' : 'opacity-0'
          )}
        />

        {/* Bottom Sheet with slide-up animation */}
        <FocusScope contain restoreFocus autoFocus>
          <div
            {...overlayProps}
            ref={sheetRef}
            className={cn(
              DEFAULT_STYLES.bottomSheet,
              'transform transition-transform duration-300 ease-out',
              isOpen ? 'translate-y-0' : 'translate-y-full',
              className,
              'base__bottom__sheet'
            )}
            style={{
              minHeight: '30vh',
              maxHeight: availableHeight
                ? `${Math.min(availableHeight, window.innerHeight * 0.95)}px`
                : '80vh',
              height: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <DismissButton onDismiss={handleClose} />

            {/* Header */}
            {(title || showCloseButton) && (
              <div className={DEFAULT_STYLES.bottomSheet__header}>
                {title && (
                  <h3 className={DEFAULT_STYLES.bottomSheet__title}>{title}</h3>
                )}
                {showCloseButton && (
                  <button
                    type="button"
                    onClick={handleClose}
                    className={DEFAULT_STYLES.bottomSheet__closeButton}
                    aria-label="Close"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="#475569"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            )}

            {/* Content - scrollable area */}
            <div
              ref={contentRef}
              className={DEFAULT_STYLES.bottomSheet__content}
            >
              {children}
            </div>

            <DismissButton onDismiss={handleClose} />
          </div>
        </FocusScope>
      </div>
    </Portal>
  );
};

BottomSheet.displayName = 'BottomSheet';
